import { useEffect } from "react";

// Canonical brand domain (used for canonical URLs + OG). Change if the primary domain changes.
export const SITE_URL = "https://www.tradrquant.com";
const DEFAULT_IMAGE = `${SITE_URL}/tq-logo.png`;

// Stable node ids so schema across pages links to one Organization + one founder.
export const ORG_ID = `${SITE_URL}/#org`;
export const PERSON_ID = `${SITE_URL}/#founder`;

// Founder identity (JC Rod / Tradernaut) — attached as author/founder across schema for E-E-A-T & SEO.
export const founderPerson = {
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": PERSON_ID,
  name: "JC Rod",
  alternateName: ["Tradernaut", "Juan Carlos Rodriguez"],
  jobTitle: "Founder",
  description:
    "Founder of TradrQuant — an independently funded proprietary trader (TopStep, Apex, Lucid) building professional-grade quant tools for retail traders.",
  url: `${SITE_URL}/about`,
  worksFor: { "@id": ORG_ID },
  sameAs: ["https://invoapp.com/join/tradernauts", "https://t.me/TradrQuantRadar", "https://verify.tradrquant.com"],
};

// Compact author reference (points at the full Person node above).
const authorRef = {
  "@type": "Person",
  "@id": PERSON_ID,
  name: "JC Rod",
  alternateName: "Tradernaut",
  url: `${SITE_URL}/about`,
};

// TradrQuant Pro indicator as a SoftwareApplication, authored by JC Rod (Tradernaut).
// Injected on the homepage at load so name + alias + software are indexed together.
export const SOFTWARE_APP = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "TradrQuant Pro",
  applicationCategory: "FinanceApplication",
  operatingSystem: "Web, TradingView",
  url: `${SITE_URL}/tools/tradrquant-pro-indicator`,
  downloadUrl: "https://www.tradingview.com/script/MiXDrieo-TradrQuant-Pro/",
  description:
    "Free, non-repainting TradingView indicator with a dual-gate confluence model and tiered signals (Standard, Strong, Premium, Scalp), by TradrQuant.",
  author: authorRef,
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  screenshot: [
    { "@type": "ImageObject", url: `${SITE_URL}/indicator/btc.webp`, caption: "BTC 5 Min — Premium (Φ) confluence signal" },
    { "@type": "ImageObject", url: `${SITE_URL}/indicator/mnq.webp`, caption: "NQ 5 Min — Premium (Φ) confluence signal" },
  ],
};

// Injects one or more JSON-LD structured-data blocks. `data` is an object or array of objects.
export const JsonLd = ({ data }) => {
  const blocks = Array.isArray(data) ? data : [data];
  return (
    <>
      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(block) }} />
      ))}
    </>
  );
};

// Organization + WebSite (with SearchAction) schema for the homepage.
export const siteJsonLd = () => [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: "TradrQuant",
    url: SITE_URL,
    logo: DEFAULT_IMAGE,
    description:
      "TradrQuant is a low-latency crypto trading terminal for retail traders — real-time multi-exchange order flow, liquidations and whale data from Hyperliquid, Binance, OKX, Bybit and Coinbase, with a verified track record and a free Academy.",
    founder: { "@id": PERSON_ID },
    sameAs: ["https://t.me/TradrQuantRadar", "https://verify.tradrquant.com", "https://www.instagram.com/tradrquant/"],
  },
  founderPerson,
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "TradrQuant",
    url: SITE_URL,
    publisher: { "@id": ORG_ID },
    author: { "@id": PERSON_ID },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/learn?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  },
  SOFTWARE_APP,
];


// Build Article + BreadcrumbList + FAQPage schema for a lesson.
export const lessonJsonLd = (lesson, categoryLabel) => {
  const url = `${SITE_URL}/learn/${lesson.slug}`;
  const graph = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: lesson.title,
      description: lesson.summary,
      articleSection: categoryLabel,
      inLanguage: "en",
      image: DEFAULT_IMAGE,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      author: authorRef,
      publisher: {
        "@type": "Organization",
        name: "TradrQuant",
        logo: { "@type": "ImageObject", url: DEFAULT_IMAGE },
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Academy", item: `${SITE_URL}/learn` },
        { "@type": "ListItem", position: 3, name: lesson.title, item: url },
      ],
    },
  ];
  if (lesson.faqs && lesson.faqs.length) {
    graph.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: lesson.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }
  return graph;
};


// Build TechArticle + BreadcrumbList + FAQPage schema for a research paper.
export const paperJsonLd = (paper, trackLabel) => {
  const url = `${SITE_URL}/research/${paper.slug}`;
  const graph = [
    {
      "@context": "https://schema.org",
      "@type": "TechArticle",
      headline: paper.title,
      description: paper.abstract,
      articleSection: trackLabel,
      inLanguage: "en",
      image: DEFAULT_IMAGE,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      author: authorRef,
      publisher: {
        "@type": "Organization",
        name: "TradrQuant",
        logo: { "@type": "ImageObject", url: DEFAULT_IMAGE },
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Quant Lab", item: `${SITE_URL}/research` },
        { "@type": "ListItem", position: 3, name: paper.title, item: url },
      ],
    },
  ];
  if (paper.faqs && paper.faqs.length) {
    graph.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: paper.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }
  return graph;
};


const upsertMeta = (selector, attr, key, content) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export const useSeo = ({ title, description, path = "/", image = DEFAULT_IMAGE, type = "website" }) => {
  useEffect(() => {
    const url = `${SITE_URL}${path}`;
    if (title) document.title = title;
    if (description) {
      upsertMeta('meta[name="description"]', "name", "description", description);
      upsertMeta('meta[property="og:description"]', "property", "og:description", description);
      upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    }
    if (title) {
      upsertMeta('meta[property="og:title"]', "property", "og:title", title);
      upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    }
    upsertMeta('meta[property="og:type"]', "property", "og:type", type);
    upsertMeta('meta[property="og:url"]', "property", "og:url", url);
    upsertMeta('meta[property="og:image"]', "property", "og:image", image);
    upsertMeta('meta[property="og:site_name"]', "property", "og:site_name", "TradrQuant");
    upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", image);
    upsertMeta('meta[name="author"]', "name", "author", "JC Rod (Tradernaut)");
    if (type === "article") {
      upsertMeta('meta[property="article:author"]', "property", "article:author", `${SITE_URL}/about`);
    }
    upsertLink("canonical", url);
  }, [title, description, path, image, type]);
};
